import {
  createContext,
  type FormEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
  Heading,
  Modal,
  ModalOverlay,
  Popover,
  Menu,
  MenuItem,
  MenuTrigger,
  Separator
} from "react-aria-components";
import { Check, ChevronDown, LoaderCircle, MoreHorizontal, X } from "lucide-react";

type Tone = "neutral" | "success" | "warning" | "danger";

export function Button({
  children,
  tone = "neutral",
  iconOnly = false,
  loading = false,
  className = "",
  ...props
}: React.ComponentProps<typeof AriaButton> & {
  tone?: "neutral" | "primary" | "danger" | "quiet";
  iconOnly?: boolean;
  loading?: boolean;
}) {
  return (
    <AriaButton
      {...props}
      isDisabled={props.isDisabled || loading}
      className={`button button-${tone}${iconOnly ? " button-icon" : ""} ${className}`.trim()}
    >
      {loading ? <LoaderCircle className="spin" aria-hidden /> : children}
    </AriaButton>
  );
}

export function StatusBadge({ value }: { value: string }) {
  const normalized = value.toLowerCase();
  const tone: Tone =
    normalized === "approved" || normalized === "active" || normalized === "sent" || normalized === "available" || normalized === "finished"
      ? "success"
      : normalized === "pending" || normalized === "invited" || normalized === "prepared" || normalized === "maintenance" || normalized === "scheduled_maintenance" || normalized === "planned_shutdown" || normalized === "in_progress"
        ? "warning"
        : normalized === "rejected" || normalized === "disabled" || normalized === "failed" || normalized === "breakdown" || normalized === "out_of_service" || normalized === "inactive"
          ? "danger"
          : "neutral";
  return (
    <span className={`status-badge status-${tone}`}>
      <span aria-hidden />
      {value === "planned_shutdown" ? "Planned shutdown / holiday closure" : value.replace(/_/g, " ")}
    </span>
  );
}

export function Avatar({ name, size = "medium" }: { name: string; size?: "small" | "medium" }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";
  return <span className={`avatar avatar-${size}`}>{initials}</span>;
}

export function EmptyState({
  icon,
  title,
  description,
  action
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <strong>{title}</strong>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  className = ""
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode | ((id: string) => ReactNode);
  className?: string;
}) {
  const id = useId();
  return (
    <label className={`field ${className}`} htmlFor={id}>
      <span className="field-label">{label}</span>
      {typeof children === "function" ? children(id) : children}
      {error ? <span className="field-error">{error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  width = "medium",
  movable = false,
  children,
  footer
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  width?: "medium" | "wide";
  movable?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const dragRef = useRef<{ offsetX: number; offsetY: number } | null>(null);
  const [position, setPosition] = useState({ x: 24, y: 24 });
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useLayoutEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onCloseRef.current();
    document.addEventListener("keydown", onKey);
    document.body.classList.add("drawer-open");
    if (movable) {
      setPosition({
        x: Math.max(16, window.innerWidth - Math.min(720, window.innerWidth - 32) - 24),
        y: 24
      });
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("drawer-open");
    };
  }, [open, movable]);
  if (!open) return null;
  return (
    <div className={`drawer-layer ${movable ? "drawer-layer-movable" : ""}`} role="presentation">
      {!movable && <button className="drawer-scrim" aria-label="Close panel" onClick={onClose} />}
      <aside
        className={`drawer drawer-${width} ${movable ? "drawer-movable" : ""}`}
        role="dialog"
        aria-modal={!movable}
        aria-labelledby="drawer-title"
        style={movable ? { left: position.x, top: position.y } : undefined}
      >
        <header
          className={`drawer-header ${movable ? "drawer-drag-handle" : ""}`}
          onPointerDown={movable ? (event) => {
            if (event.button !== 0 || (event.target as HTMLElement).closest("button, input, select, textarea, a")) return;
            const rect = event.currentTarget.parentElement!.getBoundingClientRect();
            dragRef.current = { offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top };
            event.currentTarget.setPointerCapture(event.pointerId);
          } : undefined}
          onPointerMove={movable ? (event) => {
            if (!dragRef.current) return;
            const width = event.currentTarget.parentElement!.getBoundingClientRect().width;
            const height = event.currentTarget.parentElement!.getBoundingClientRect().height;
            setPosition({
              x: Math.max(8, Math.min(window.innerWidth - width - 8, event.clientX - dragRef.current.offsetX)),
              y: Math.max(8, Math.min(window.innerHeight - height - 8, event.clientY - dragRef.current.offsetY))
            });
          } : undefined}
          onPointerUp={movable ? (event) => {
            dragRef.current = null;
            event.currentTarget.releasePointerCapture(event.pointerId);
          } : undefined}
        >
          <div>
            <h2 id="drawer-title">{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button ref={closeRef} className="icon-plain" onClick={onClose} aria-label="Close panel">
            <X aria-hidden />
          </button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer && <footer className="drawer-footer">{footer}</footer>}
      </aside>
    </div>
  );
}

export function ConfirmDialog({
  trigger,
  title,
  description,
  children,
  confirmLabel,
  danger,
  onConfirm
}: {
  trigger: ReactNode;
  title: string;
  description: string;
  children?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  return (
    <DialogTrigger>
      {trigger}
      <ModalOverlay className="modal-overlay">
        <Modal className="modal">
          <Dialog className="confirm-dialog">
            {({ close }) => (
              <>
                <Heading slot="title">{title}</Heading>
                <p>{description}</p>
                {children}
                <div className="dialog-actions">
                  <Button onPress={close}>Cancel</Button>
                  <Button
                    tone={danger ? "danger" : "primary"}
                    onPress={async () => {
                      await onConfirm();
                      close();
                    }}
                  >
                    {confirmLabel}
                  </Button>
                </div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
}

export function ActionMenu({
  label = "Open actions",
  items
}: {
  label?: string;
  items: Array<{ id: string; label: string; icon?: ReactNode; danger?: boolean; disabled?: boolean; action: () => void } | "separator">;
}) {
  return (
    <MenuTrigger>
      <Button iconOnly tone="quiet" aria-label={label}>
        <MoreHorizontal aria-hidden />
      </Button>
      <Popover className="menu-popover" placement="bottom end">
        <Menu
          aria-label={label}
          onAction={(key) => {
            const item = items.find((candidate) => candidate !== "separator" && candidate.id === key);
            if (item && item !== "separator") item.action();
          }}
        >
          {items.map((item, index) =>
            item === "separator" ? (
              <Separator key={`separator-${index}`} />
            ) : (
              <MenuItem
                id={item.id}
                key={item.id}
                isDisabled={item.disabled}
                className={item.danger ? "menu-danger" : ""}
                textValue={item.label}
              >
                {item.icon}
                <span>{item.label}</span>
              </MenuItem>
            )
          )}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

export function SelectControl({
  value,
  onChange,
  children,
  ariaLabel
}: {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  ariaLabel: string;
}) {
  return (
    <span className="select-control">
      <select value={value} onChange={(event) => onChange(event.target.value)} aria-label={ariaLabel}>
        {children}
      </select>
      <ChevronDown aria-hidden />
    </span>
  );
}

type Notification = { id: number; message: string; tone: Tone };
const NotificationContext = createContext<(message: string, tone?: Tone) => void>(() => undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const notify = useCallback((message: string, tone: Tone = "neutral") => {
    const id = Date.now() + Math.random();
    setNotifications((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => setNotifications((current) => current.filter((item) => item.id !== id)), 4200);
  }, []);
  return (
    <NotificationContext.Provider value={notify}>
      {children}
      <div className="toast-region" aria-live="polite">
        {notifications.map((item) => (
          <div key={item.id} className={`toast toast-${item.tone}`}>
            {item.tone === "success" && <Check aria-hidden />}
            <span>{item.message}</span>
            <button onClick={() => setNotifications((current) => current.filter((candidate) => candidate.id !== item.id))} aria-label="Dismiss">
              <X aria-hidden />
            </button>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

export const useNotify = () => useContext(NotificationContext);

export function AsyncForm({
  onSubmit,
  children,
  className
}: {
  onSubmit: (form: FormData) => Promise<void>;
  children: ReactNode;
  className?: string;
}) {
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit(new FormData(event.currentTarget));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <form className={className} onSubmit={handleSubmit} data-submitting={submitting || undefined}>
      {children}
    </form>
  );
}
